import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Field, Input } from '@/components/ui/Field';

import { DEFAULT_OPTIONS, processImage, type CleanupOptions } from './imageProcessing';

interface ImageCleanupDialogProps {
  open: boolean;
  sourceUrl: string | null;
  onCancel: () => void;
  onApply: (cleanedUrl: string) => void;
}

type LoadState = 'idle' | 'loading' | 'ready' | 'error';

const PREVIEW_MAX_SIDE = 1200;

const CHECKER_BG = {
  backgroundColor: 'rgb(var(--canvas))',
  backgroundImage:
    'linear-gradient(45deg, rgba(148, 163, 184, 0.28) 25%, transparent 25%),' +
    'linear-gradient(-45deg, rgba(148, 163, 184, 0.28) 25%, transparent 25%),' +
    'linear-gradient(45deg, transparent 75%, rgba(148, 163, 184, 0.28) 75%),' +
    'linear-gradient(-45deg, transparent 75%, rgba(148, 163, 184, 0.28) 75%)',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
  backgroundSize: '16px 16px',
};

function canvasToObjectUrl(canvas: HTMLCanvasElement): Promise<string> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (!blob) {
        reject(new Error('Could not export cleaned image'));
        return;
      }
      resolve(URL.createObjectURL(blob));
    }, 'image/png');
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Image load failed'));
    image.src = src;
  });
}

function resetOptions(): CleanupOptions {
  return { ...DEFAULT_OPTIONS };
}

export function ImageCleanupDialog({ open, sourceUrl, onCancel, onApply }: ImageCleanupDialogProps) {
  const [options, setOptions] = useState<CleanupOptions>(() => resetOptions());
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [autoThreshold, setAutoThreshold] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const frameRef = useRef<number | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  const revokePreviewObjectUrl = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }, []);

  const clearPreviewUrl = useCallback(() => {
    revokePreviewObjectUrl();
    setPreviewUrl(null);
  }, [revokePreviewObjectUrl]);

  const thresholdMode = options.threshold == null ? 'Auto' : 'Manual';
  const thresholdValue = options.threshold ?? autoThreshold ?? 128;

  const sourceLabel = useMemo(() => {
    if (!image) return 'No image';
    return `${image.naturalWidth || image.width} x ${image.naturalHeight || image.height}`;
  }, [image]);

  useEffect(() => {
    if (!open) return;
    setOptions(resetOptions());
  }, [open, sourceUrl]);

  useEffect(() => {
    if (!open || !sourceUrl) {
      setImage(null);
      setLoadState(open ? 'idle' : 'idle');
      return;
    }

    let cancelled = false;
    setLoadState('loading');
    setError(null);
    setImage(null);

    loadImage(sourceUrl)
      .then(nextImage => {
        if (cancelled) return;
        setImage(nextImage);
        setLoadState('ready');
      })
      .catch(err => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Image load failed');
        setLoadState('error');
      });

    return () => { cancelled = true; };
  }, [open, sourceUrl]);

  useEffect(() => {
    if (frameRef.current != null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    if (!open || !image || loadState !== 'ready') {
      clearPreviewUrl();
      setAutoThreshold(null);
      return;
    }

    let cancelled = false;
    setIsProcessing(true);
    setError(null);

    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      try {
        const result = processImage(image, options, PREVIEW_MAX_SIDE);
        canvasToObjectUrl(result.canvas)
          .then(url => {
            if (cancelled) {
              URL.revokeObjectURL(url);
              return;
            }
            if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
            previewUrlRef.current = url;
            setPreviewUrl(url);
            setAutoThreshold(result.autoThreshold);
          })
          .catch(err => {
            if (!cancelled) setError(err instanceof Error ? err.message : 'Preview export failed');
          })
          .finally(() => {
            if (!cancelled) setIsProcessing(false);
          });
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Preview processing failed');
          setIsProcessing(false);
        }
      }
    });

    return () => {
      cancelled = true;
      if (frameRef.current != null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [clearPreviewUrl, image, loadState, open, options]);

  useEffect(() => () => {
    if (frameRef.current != null) cancelAnimationFrame(frameRef.current);
    revokePreviewObjectUrl();
  }, [revokePreviewObjectUrl]);

  const updateOption = <K extends keyof CleanupOptions>(key: K, value: CleanupOptions[K]) => {
    setOptions(current => ({ ...current, [key]: value }));
  };

  const handleReset = () => {
    setOptions(resetOptions());
    setError(null);
  };

  const handleApply = async () => {
    if (!image) return;
    setIsApplying(true);
    setError(null);
    try {
      const result = processImage(image, options);
      const url = await canvasToObjectUrl(result.canvas);
      onApply(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Full-size processing failed');
    } finally {
      setIsApplying(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-4 py-6">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="image-cleanup-title"
        className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-border bg-surface text-ink shadow-xl"
      >
        <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
          <div>
            <h2 id="image-cleanup-title" className="font-sans text-sm font-semibold">Очистка изображения</h2>
            <p className="mt-1 text-[11px] text-ink-4">{sourceLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={handleReset} disabled={isApplying}>Reset</Button>
            <Button size="sm" onClick={onCancel} disabled={isApplying}>Cancel</Button>
            <Button
              size="sm"
              variant="primary"
              onClick={handleApply}
              loading={isApplying}
              disabled={!image || loadState !== 'ready'}
            >
              Apply
            </Button>
          </div>
        </header>

        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[1fr_280px]">
          <div className="grid min-h-[420px] grid-cols-1 gap-3 overflow-auto bg-page p-3 md:grid-cols-2">
            <PreviewPanel title="До" loading={loadState === 'loading'} error={loadState === 'error' ? error : null}>
              {sourceUrl && loadState === 'ready' ? (
                <img src={sourceUrl} alt="Before cleanup" className="max-h-full max-w-full object-contain" />
              ) : null}
            </PreviewPanel>

            <PreviewPanel title="После" loading={isProcessing} error={error}>
              {previewUrl ? (
                <div className="flex h-full w-full items-center justify-center rounded border border-border" style={CHECKER_BG}>
                  <img src={previewUrl} alt="After cleanup preview" className="max-h-full max-w-full object-contain" />
                </div>
              ) : null}
            </PreviewPanel>
          </div>

          <aside className="flex flex-col gap-4 overflow-auto border-t border-border bg-surface-2 p-4 lg:border-l lg:border-t-0">
            <RangeControl
              label="Brightness"
              min={-80}
              max={80}
              value={options.brightness}
              onChange={value => updateOption('brightness', value)}
            />
            <RangeControl
              label="Contrast"
              min={-80}
              max={80}
              value={options.contrast}
              onChange={value => updateOption('contrast', value)}
            />

            <div className="flex flex-col gap-2 rounded-md border border-border bg-surface p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-ink-2">Threshold</span>
                <span className="font-mono text-[11px] text-ink-4">
                  {thresholdMode} · {thresholdValue}
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={255}
                step={1}
                value={thresholdValue}
                onChange={event => updateOption('threshold', Number(event.target.value))}
                className="w-full accent-accent"
              />
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] text-ink-4">
                  Auto: {autoThreshold ?? '...'}
                </span>
                <Button size="sm" onClick={() => updateOption('threshold', null)}>
                  Auto
                </Button>
              </div>
            </div>

            <RangeControl
              label="Thickness"
              min={0}
              max={3}
              value={options.thicken}
              onChange={value => updateOption('thicken', value)}
            />

            <div className="grid gap-2">
              <CheckboxControl
                label="Denoise"
                checked={options.denoise}
                onChange={checked => updateOption('denoise', checked)}
              />
              <CheckboxControl
                label="Transparent white"
                checked={options.transparentWhite}
                onChange={checked => updateOption('transparentWhite', checked)}
              />
              <CheckboxControl
                label="Invert"
                checked={options.invert}
                onChange={checked => updateOption('invert', checked)}
              />
            </div>

            {error && (
              <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
                {error}
              </div>
            )}
          </aside>
        </div>
      </section>
    </div>
  );
}

function PreviewPanel({
  title,
  loading,
  error,
  children,
}: {
  title: string;
  loading: boolean;
  error: string | null;
  children: React.ReactNode;
}) {
  return (
    <section className="flex min-h-[360px] flex-col overflow-hidden rounded-lg border border-border bg-surface">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <h3 className="font-sans text-sm font-semibold text-ink">{title}</h3>
        {loading && <span className="text-[11px] text-ink-4">Loading...</span>}
      </div>
      <div className="flex min-h-0 flex-1 items-center justify-center p-3">
        {error ? (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">{error}</div>
        ) : children}
      </div>
    </section>
  );
}

function RangeControl({
  label,
  min,
  max,
  value,
  onChange,
}: {
  label: string;
  min: number;
  max: number;
  value: number;
  onChange: (value: number) => void;
}) {
  const id = `cleanup-${label.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <Field label={label} htmlFor={id}>
      <div className="grid grid-cols-[1fr_64px] items-center gap-2">
        <input
          id={id}
          type="range"
          min={min}
          max={max}
          step={1}
          value={value}
          onChange={event => onChange(Number(event.target.value))}
          className="w-full accent-accent"
        />
        <Input
          type="number"
          min={min}
          max={max}
          value={value}
          onChange={event => onChange(Number(event.target.value))}
          className="font-mono text-xs"
        />
      </div>
    </Field>
  );
}

function CheckboxControl({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-xs font-medium text-ink-2">
      <input
        type="checkbox"
        checked={checked}
        onChange={event => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-border-2 bg-surface-2"
        style={{ accentColor: 'rgb(var(--accent))' }}
      />
      {label}
    </label>
  );
}
